/**
 * Judge.me Bewertungen – Slider / Grid
 *
 * Laedt die Bewertungen ueber den Cloudflare-Worker-Proxy (der private
 * Judge.me-Token liegt ausschliesslich dort) und rendert sie entweder als
 * horizontalen Slider oder als Grid mit "Mehr anzeigen".
 *
 * Alle sichtbaren Texte kommen uebersetzt per data-Attribut aus Liquid,
 * damit die Section in allen Storefront-Sprachen korrekt ist.
 */

const JM_PRODUCT_CACHE = new Map();

async function jmFetchProduct(handle) {
  if (!handle) return null;
  if (JM_PRODUCT_CACHE.has(handle)) return JM_PRODUCT_CACHE.get(handle);
  const promise = (async () => {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const p = await res.json();
      return {
        title: p.title,
        url: p.url,
        image: p.featured_image || (p.images && p.images[0]) || null,
      };
    } catch {
      return null;
    }
  })();
  JM_PRODUCT_CACHE.set(handle, promise);
  return promise;
}

class JudgemeReviews extends HTMLElement {
  constructor() {
    super();
    this.reviews = [];
    this.currentPage = 1;
    this.hasMore = true;
    this.isLoadingMore = false;
  }

  connectedCallback() {
    const d = this.dataset;
    this.proxyUrl = (d.proxyUrl || '').trim().replace(/\/$/, '');
    this.layout = d.layout === 'grid' ? 'grid' : 'slider';
    this.perPage = parseInt(d.perPage, 10) || 30;
    this.gridColumns = parseInt(d.gridColumns, 10) || 3;
    this.minRating = parseInt(d.minRating, 10) || 0;
    this.showProduct = d.showProduct === 'true';
    this.replyShopName = (d.replyShopName || '').trim() || null;
    this.shopLogoUrl = (d.shopLogoUrl || '').trim() || null;
    this.shopUrl = (d.shopUrl || '').trim() || null;
    this.locale = (d.locale || 'de').trim();
    this.buttonClass = (d.buttonClass || 'button button--secondary').trim();
    // Judge.me hat beim Plattform-Import den Kaeufer-Status nicht uebernommen.
    // Die importierten Bewertungen stammen dennoch von echten Kaeufern, daher
    // laesst sich das Badge in den Section-Einstellungen dauerhaft aktivieren.
    this.alwaysVerified = d.alwaysVerified === 'true';
    this.verifiedIcon = (d.verifiedIcon || '').trim() || null;

    // Uebersetzte Texte
    this.t = {
      loadMore: d.tLoadMore || 'Mehr anzeigen',
      showMore: d.tShowMore || 'Mehr anzeigen',
      showLess: d.tShowLess || 'Weniger anzeigen',
      verified: d.tVerified || 'Verifizierter Kauf',
      reviewedProduct: d.tReviewedProduct || 'Bewertetes Produkt',
      companyReview: d.tCompanyReview || 'Firmenbewertung',
      replyFrom: d.tReplyFrom || 'Antwort von %%SHOP%%',
      replyGeneric: d.tReplyGeneric || 'Antwort vom Shop',
      noReviews: d.tNoReviews || 'Noch keine Bewertungen vorhanden.',
      loadError: d.tLoadError || 'Bewertungen konnten nicht geladen werden.',
      anonymous: d.tAnonymous || 'Anonym',
      prev: d.tPrev || 'Zurueck',
      next: d.tNext || 'Weiter',
      starsAria: d.tStarsAria || '%%RATING%% / 5',
    };

    this.classList.toggle('jm-reviews--grid', this.layout === 'grid');
    this.classList.toggle('jm-reviews--slider', this.layout === 'slider');
    if (this.layout === 'grid') {
      this.style.setProperty('--jm-grid-cols', String(this.gridColumns));
    }

    if (!this.proxyUrl) {
      this._renderStatus('Worker-URL fehlt – bitte in den Section-Einstellungen eintragen.');
      return;
    }

    this._renderSkeleton();
    this._loadReviews();
  }

  async _fetchPage(page) {
    const params = new URLSearchParams({ per_page: String(this.perPage), page: String(page) });
    if (this.minRating === 5) params.set('rating', '5');
    const res = await fetch(`${this.proxyUrl}/api/reviews?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    const raw = Array.isArray(data.reviews) ? data.reviews : [];
    let filtered = raw;
    if (this.minRating > 0) filtered = filtered.filter((r) => r.rating >= this.minRating);
    return { filtered, hasMore: data.has_more === true };
  }

  async _loadReviews() {
    try {
      const { filtered, hasMore } = await this._fetchPage(1);
      this.currentPage = 1;
      this.hasMore = hasMore;
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      this.reviews = filtered;

      if (this.reviews.length === 0) {
        this._renderStatus(this.t.noReviews);
        return;
      }
      this._render();
    } catch (err) {
      console.error('[judgeme-reviews] Fetch failed:', err);
      this._renderStatus(this.t.loadError);
    }
  }

  async _loadMore() {
    if (this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this._setLoadMoreLoading(true);
    try {
      const { filtered, hasMore } = await this._fetchPage(this.currentPage + 1);
      this.currentPage += 1;
      this.hasMore = hasMore;

      const seen = new Set(this.reviews.map((r) => r.id));
      const fresh = filtered.filter((r) => !seen.has(r.id));
      this.reviews = this.reviews.concat(fresh);

      if (this.track && fresh.length > 0) {
        this.track.insertAdjacentHTML('beforeend', fresh.map((r) => this._buildCard(r)).join(''));
        if (this.showProduct) this._hydrateProducts();
        this._setupExpand();
      }
      if (!this.hasMore && this.loadMoreContainer) {
        this.loadMoreContainer.remove();
        this.loadMoreContainer = null;
        this.loadMoreBtn = null;
      }
    } catch (err) {
      console.error('[judgeme-reviews] Load more failed:', err);
    } finally {
      this.isLoadingMore = false;
      this._setLoadMoreLoading(false);
    }
  }

  _setLoadMoreLoading(loading) {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.classList.toggle('is-loading', loading);
    this.loadMoreBtn.disabled = loading;
  }

  _render() {
    const cards = this.reviews.map((r) => this._buildCard(r)).join('');

    if (this.layout === 'grid') {
      this.innerHTML = `
        <div class="jm-reviews__viewport">
          <div class="jm-reviews__track" data-track>${cards}</div>
        </div>
        <div class="jm-reviews__load-more" data-load-more-container>
          <button type="button" class="${this._escape(this.buttonClass)} jm-reviews__load-more-btn" data-load-more>
            <span class="jm-reviews__load-more-spinner" aria-hidden="true"></span>
            <span>${this._escape(this.t.loadMore)}</span>
          </button>
        </div>`;
      this.track = this.querySelector('[data-track]');
      this.loadMoreContainer = this.querySelector('[data-load-more-container]');
      this.loadMoreBtn = this.querySelector('[data-load-more]');
      this.loadMoreBtn?.addEventListener('click', () => this._loadMore());
      if (!this.hasMore && this.loadMoreContainer) {
        this.loadMoreContainer.remove();
        this.loadMoreContainer = null;
        this.loadMoreBtn = null;
      }
      if (this.showProduct) this._hydrateProducts();
      this._setupExpand();
      return;
    }

    const showControls = this.reviews.length > 1;
    this.innerHTML = `
      <div class="jm-reviews__viewport">
        <div class="jm-reviews__track" data-track>${cards}</div>
      </div>
      ${
        showControls
          ? `<div class="jm-reviews__controls">
               <button type="button" class="jm-reviews__nav-btn" data-prev aria-label="${this._escape(this.t.prev)}" disabled>
                 <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12L6 8l4-4"/></svg>
               </button>
               <div class="jm-reviews__progress"><div class="jm-reviews__progress-thumb" data-progress-thumb></div></div>
               <button type="button" class="jm-reviews__nav-btn" data-next aria-label="${this._escape(this.t.next)}">
                 <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4l4 4-4 4"/></svg>
               </button>
             </div>`
          : ''
      }`;

    this.track = this.querySelector('[data-track]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.progressThumb = this.querySelector('[data-progress-thumb]');

    if (showControls) this._bindCarousel();
    if (this.showProduct) this._hydrateProducts();
    this._setupExpand();
  }

  async _hydrateProducts() {
    const handles = [...new Set(this.reviews.map((r) => r.product_handle).filter(Boolean))];
    const results = await Promise.all(handles.map(async (h) => [h, await jmFetchProduct(h)]));
    const byHandle = Object.fromEntries(results);

    this.querySelectorAll('[data-product-slot]').forEach((slot) => {
      const product = byHandle[slot.dataset.productSlot];
      if (!product) {
        slot.outerHTML = this._buildShopCardHtml();
        return;
      }
      const imageHtml = product.image
        ? `<img class="jm-reviews__product-image" src="${this._escape(product.image)}" alt="" loading="lazy" width="48" height="48">`
        : '<div class="jm-reviews__product-image" aria-hidden="true"></div>';
      slot.outerHTML = `
        <a class="jm-reviews__product-card" href="${this._escape(product.url)}">
          ${imageHtml}
          <div class="jm-reviews__product-info">
            <span class="jm-reviews__product-label">${this._escape(this.t.reviewedProduct)}</span>
            <span class="jm-reviews__product-title">${this._escape(product.title)}</span>
          </div>
          <svg class="jm-reviews__product-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 4l4 4-4 4"/></svg>
        </a>`;
    });
  }

  _buildShopCardHtml() {
    const shopName = this.replyShopName || '';
    const open = this.shopUrl
      ? `<a class="jm-reviews__product-card" href="${this._escape(this.shopUrl)}">`
      : '<div class="jm-reviews__product-card">';
    const close = this.shopUrl ? '</a>' : '</div>';
    const chevron = this.shopUrl
      ? '<svg class="jm-reviews__product-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 4l4 4-4 4"/></svg>'
      : '';
    const imageHtml = this.shopLogoUrl
      ? `<img class="jm-reviews__product-image jm-reviews__product-image--logo" src="${this._escape(this.shopLogoUrl)}" alt="${this._escape(shopName)}" loading="lazy" width="48" height="48">`
      : `<div class="jm-reviews__product-image jm-reviews__product-image--shop" aria-hidden="true">${this._escape((shopName.charAt(0) || '?').toUpperCase())}</div>`;
    return `
      ${open}
        ${imageHtml}
        <div class="jm-reviews__product-info">
          <span class="jm-reviews__product-label">${this._escape(this.t.companyReview)}</span>
          <span class="jm-reviews__product-title">${this._escape(shopName)}</span>
        </div>
        ${chevron}
      ${close}`;
  }

  _buildCard(r) {
    const rating = Math.max(0, Math.min(5, parseInt(r.rating, 10) || 0));
    const stars = Array.from({ length: 5 }, (_, i) =>
      i < rating ? '★' : '<span class="jm-reviews__stars--empty">★</span>'
    ).join('');

    const date = this._formatDate(r.created_at);
    const authorName = r.reviewer?.name || this.t.anonymous;
    const initial = authorName.trim().charAt(0).toUpperCase() || '?';
    const isVerified = this.alwaysVerified || r.verified === true;
    const title = r.title ? this._escape(r.title) : '';
    const body = this._escape(r.body || '');
    const starsAria = this.t.starsAria.replace('%%RATING%%', String(rating));

    const verifiedIconHtml = this.verifiedIcon
      ? `<img class="jm-reviews__verified-icon" src="${this._escape(this.verifiedIcon)}" alt="" loading="lazy" width="16" height="16">`
      : '';
    // Nur das eigene Icon - der zusaetzliche Haken wirkte doppelt gemoppelt.
    // Ist kein Icon gesetzt, greift ein schlichter Haken als Fallback.
    const verifiedMark = this.verifiedIcon
      ? verifiedIconHtml
      : '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8l3.5 3.5L13 5"/></svg>';
    const verifiedBadge = isVerified
      ? `<span class="jm-reviews__verified">
           ${verifiedMark}
           ${this._escape(this.t.verified)}
         </span>`
      : '';

    const isShopReview = !r.product_external_id || r.product_handle === 'judgeme-shop-reviews';
    let productSlot = '';
    if (this.showProduct) {
      productSlot =
        !isShopReview && r.product_handle
          ? `<div class="jm-reviews__product-placeholder" data-product-slot="${this._escape(r.product_handle)}"></div>`
          : this._buildShopCardHtml();
    }

    let replyBlock = '';
    if (r.reply && r.reply.content) {
      const shopName = r.reply.shop_name || this.replyShopName;
      const headerLabel = shopName
        ? this._escape(this.t.replyFrom.replace('%%SHOP%%', shopName))
        : this._escape(this.t.replyGeneric);
      replyBlock = `
        <div class="jm-reviews__reply">
          <div class="jm-reviews__reply-header">
            <svg class="jm-reviews__reply-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 14 4 9 9 4"/><path d="M4 9h7a4 4 0 0 1 4 4v0"/></svg>
            <span class="jm-reviews__reply-author">${headerLabel}</span>
          </div>
          <div class="jm-reviews__reply-body" data-reply-body>${this._escape(r.reply.content)}</div>
          <button type="button" class="jm-reviews__reply-toggle" data-reply-toggle hidden aria-expanded="false">${this._escape(this.t.showMore)}</button>
        </div>`;
    }

    return `
      <article class="jm-reviews__card">
        <div class="jm-reviews__card-top">
          <div class="jm-reviews__stars" aria-label="${this._escape(starsAria)}">${stars}</div>
          <span class="jm-reviews__date">${date}</span>
        </div>
        ${title ? `<h3 class="jm-reviews__title">${title}</h3>` : ''}
        <p class="jm-reviews__body" data-body>${body}</p>
        <button type="button" class="jm-reviews__expand-btn" data-expand-btn hidden aria-expanded="false">${this._escape(this.t.showMore)}</button>
        <div class="jm-reviews__footer">
          <span class="jm-reviews__author">
            <span class="jm-reviews__avatar" aria-hidden="true">${this._escape(initial)}</span>
            <span>${this._escape(authorName)}</span>
          </span>
          ${verifiedBadge}
        </div>
        ${replyBlock}
        ${productSlot}
      </article>`;
  }

  _setupExpand(scope = this) {
    scope.querySelectorAll('.jm-reviews__card').forEach((card) => {
      if (card.dataset.expandChecked === '1') return;
      card.dataset.expandChecked = '1';

      this._bindClampToggle({
        body: card.querySelector('[data-body]'),
        btn: card.querySelector('[data-expand-btn]'),
        host: card,
      });

      const reply = card.querySelector('.jm-reviews__reply');
      if (reply) {
        this._bindClampToggle({
          body: reply.querySelector('[data-reply-body]'),
          btn: reply.querySelector('[data-reply-toggle]'),
          host: reply,
        });
      }
    });
  }

  _bindClampToggle({ body, btn, host }) {
    if (!body || !btn || !host) return;
    requestAnimationFrame(() => {
      if (body.scrollHeight - body.clientHeight <= 1) return;
      btn.hidden = false;
      btn.addEventListener('click', () => {
        const expanded = host.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded ? this.t.showLess : this.t.showMore;
      });
    });
  }

  _bindCarousel() {
    const cardWidth = () => {
      const card = this.track.querySelector('.jm-reviews__card');
      if (!card) return 320;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '16');
      return card.offsetWidth + gap;
    };

    this.prevBtn.addEventListener('click', () => this.track.scrollBy({ left: -cardWidth(), behavior: 'smooth' }));
    this.nextBtn.addEventListener('click', () => this.track.scrollBy({ left: cardWidth(), behavior: 'smooth' }));

    const updateProgress = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) {
        this.progressThumb.style.width = '100%';
        this.progressThumb.style.left = '0';
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
        return;
      }
      const visibleRatio = clientWidth / scrollWidth;
      this.progressThumb.style.width = visibleRatio * 100 + '%';
      this.progressThumb.style.left = (scrollLeft / maxScroll) * (1 - visibleRatio) * 100 + '%';
      this.prevBtn.disabled = scrollLeft <= 2;
      this.nextBtn.disabled = scrollLeft >= maxScroll - 2;
    };

    this.track.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    requestAnimationFrame(updateProgress);
  }

  _renderSkeleton() {
    const cards = Array.from({ length: 4 }, () => '<div class="jm-reviews__skeleton-card"></div>').join('');
    this.innerHTML = `<div class="jm-reviews__skeleton">${cards}</div>`;
  }

  _renderStatus(message) {
    this.innerHTML = `<div class="jm-reviews__status">${this._escape(message)}</div>`;
  }

  _formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '';
    }
  }

  _escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!window.customElements.get('judgeme-reviews')) {
  window.customElements.define('judgeme-reviews', JudgemeReviews);
}
